<template>
  <AppLayout :breadcrumbs="[{ label: 'Audit Trail', link: '/ticket-audit-trail' }]">
    <div class="layout-header">
      <div class="header-left">
        <SearchBar v-model="searchTerm" @update:modelValue="handleSearch" placeholder="Search audit trail..." />
        <FilterSelector :modelValue="selectedFilters" @update:modelValue="handleFilterChange" :field-options="fieldOptions" />
      </div>
    </div>

    <div class="layout-body">
      <CustomTable :columns="columns" :rows="events" :sticky-first-column="true" :loading="isLoading"
        :is-empty="events.length === 0" @row-click="handleRowClick">
        <template #column-ticket="{ row }">
          <div class="ticket-cell">
            <IconCircle :size="28" bgColor="var(--icon-ticket-bg)" color="var(--icon-ticket-color)" :icon="getTicketIcon()" />
            <span class="cell-value name-text">Ticket #{{ row.entityId }}</span>
          </div>
        </template>

        <template #column-dateCreated="{ row }">
          <span class="cell-value">{{ formatUtils.formatDate(row.dateCreated) }}</span>
        </template>

        <template #column-eventType="{ row }">
          <StatusBadge :status="eventLabel(row.eventType)" />
        </template>

        <template #column-patient="{ row }">
          <div class="patient-cell">
            <IconCircle :size="28" bgColor="var(--icon-patient-bg)" color="var(--icon-patient-color)"
              :initials="getPatientInitials(row)" />
            <span class="cell-value">{{ getPatientName(row) }}</span>
          </div>
        </template>

        <template #column-user="{ row }">
          <span class="cell-value">{{ row.userName || 'Unknown' }}</span>
        </template>

        <template #column-details="{ row }">
          <div v-if="eventSummary(row)" class="details-cell">
            <span class="cell-value">{{ eventSummary(row) }}</span>
            <Tooltip v-if="eventTooltip(row)" :text="eventTooltip(row)" position="top">
              <InformationOutlineIcon :size="14" class="details-info-icon" />
            </Tooltip>
          </div>
          <span v-else class="no-data">No details</span>
        </template>
      </CustomTable>
    </div>

    <div class="layout-footer">
      <PaginationControl :current-page="currentPage" :total-items="totalEvents" :items-per-page="itemsPerPage"
        @page-change="handlePageChange" />
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '../../components/app/AppLayout.vue';
import SearchBar from '../../components/common/SearchBar.vue';
import CustomTable from '../../components/common/CustomTable.vue';
import PaginationControl from '../../components/common/PaginationControl.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import StatusBadge from '@/components/common/StatusBadge.vue';
import FilterSelector from '../../components/common/FilterSelector.vue';
import Tooltip from '@/components/common/Tooltip.vue';
import InformationOutlineIcon from 'vue-material-design-icons/InformationOutline.vue';
import TicketIcon from 'vue-material-design-icons/Ticket.vue';
import { queryClient } from '@/libs/queryClient';
import { serializeFilters, deserializeFilters } from '@/utils/filterSerializer';
import { alertService } from '@/libs/alert';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';

const EVENT_LABELS = {
  ticket_cancelled: 'Cancelled',
  ticket_updated: 'Edited',
  ticket_deleted: 'Deleted'
};

export default {
  name: 'AuditListView',
  components: { AppLayout, SearchBar, CustomTable, PaginationControl, IconCircle, StatusBadge, FilterSelector, Tooltip, InformationOutlineIcon },
  data() {
    return {
      events: [],
      totalEvents: 0,
      currentPage: 1,
      itemsPerPage: 50,
      searchTerm: '',
      selectedFilters: [],
      isLoading: false,
      searchDebounce: null,
      columns: [
        { key: 'ticket', label: 'Ticket ID', width: '200px' },
        { key: 'dateCreated', label: 'Date', width: '200px' },
        { key: 'eventType', label: 'Event', width: '200px' },
        { key: 'patient', label: 'Patient', width: '200px' },
        { key: 'user', label: 'Performed By', width: '200px' },
        { key: 'details', label: 'Details', width: '200px' }
      ],
      fieldOptions: [
        {
          id: 'eventTypes',
          label: 'Event',
          type: 'multiselect',
          apiParams: {
            param: 'eventTypes'
          },
          options: [
            { value: 'ticket_updated', label: 'Edited' },
            { value: 'ticket_cancelled', label: 'Cancelled' },
            { value: 'ticket_deleted', label: 'Deleted' }
          ]
        },
        {
          id: 'dateCreated',
          label: 'Date',
          type: 'date',
          apiParams: {
            from: 'dateFrom',
            to: 'dateTo'
          }
        },
        {
          id: 'userId',
          label: 'Performed By',
          type: 'select',
          apiParams: {
            param: 'userId'
          },
          options: []
        }
      ],
      formatUtils
    };
  },
  created() {
    this.searchTerm = this.$route.query.search || '';
    this.selectedFilters = this.$route.query.filters ? deserializeFilters(this.$route.query.filters) : [];
    this.currentPage = parseInt(this.$route.query.page) || 1;
    this.loadFilterOptions();
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      try {
        const params = {
          entityType: 'ticket',
          limit: this.itemsPerPage,
          offset: (this.currentPage - 1) * this.itemsPerPage,
          ...this.getFilterParams()
        };
        if (this.searchTerm) params.search = this.searchTerm;

        const response = await queryClient.fetchQuery({
          queryKey: ['events', params],
          queryFn: () => api.fetchEvents(params)
        });
        this.events = response.events;
        this.totalEvents = response.total;
      } catch (err) {
        console.error('Failed to fetch audit events:', err);
        alertService.error('Failed to load audit trail: ' + err.message);
      } finally {
        this.isLoading = false;
      }
    },
    async loadFilterOptions() {
      try {
        const usersResponse = await queryClient.fetchQuery({
          queryKey: ['users'],
          queryFn: () => api.fetchUsers()
        });
        const userField = this.fieldOptions.find(f => f.id === 'userId');
        if (userField && usersResponse.users) {
          userField.options = usersResponse.users.map(user => ({
            value: user.userId,
            label: user.name || 'Unknown'
          }));
        }
      } catch (err) {
        console.error('Failed to load audit filter options:', err);
      }
    },
    getFilterParams() {
      const params = {};

      this.selectedFilters.forEach(filter => {
        const fieldConfig = this.fieldOptions.find(f => f.id === filter.field);
        if (!fieldConfig || !filter.operator) return;

        const fieldType = fieldConfig.type;

        if (fieldType === 'date') {
          if (filter.operator === 'between') {
            if (filter.from) params[fieldConfig.apiParams.from] = filter.from;
            if (filter.to) params[fieldConfig.apiParams.to] = filter.to;
          } else if (filter.operator === 'after') {
            if (filter.from) params[fieldConfig.apiParams.from] = filter.from;
          } else if (filter.operator === 'before') {
            if (filter.to) params[fieldConfig.apiParams.to] = filter.to;
          } else if (filter.operator === 'is') {
            if (filter.value) {
              params[fieldConfig.apiParams.from] = filter.value;
              params[fieldConfig.apiParams.to] = filter.value;
            }
          }
        } else if (fieldType === 'select') {
          if (filter.value !== null && filter.value !== undefined) {
            params[fieldConfig.apiParams.param] = filter.value;
          }
        } else if (fieldType === 'multiselect') {
          // An event has exactly one type, so 'is all' would always be empty —
          // both operators are treated as 'is any'.
          if (filter.values && filter.values.length > 0) {
            params[fieldConfig.apiParams.param] = filter.values.join(',');
          }
        }
      });

      return params;
    },
    handleFilterChange(filters) {
      this.selectedFilters = filters;
      this.currentPage = 1;
      this.syncUrl();
      this.load();
    },
    syncUrl() {
      const query = { ...this.$route.query };
      if (this.searchTerm) query.search = this.searchTerm; else delete query.search;
      const serialized = serializeFilters(this.selectedFilters);
      if (serialized) query.filters = serialized; else delete query.filters;
      if (this.currentPage > 1) query.page = this.currentPage; else delete query.page;
      this.$router.replace({ query });
    },
    handleSearch() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.currentPage = 1;
        this.syncUrl();
        this.load();
      }, 300);
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.syncUrl();
      this.load();
    },
    handleRowClick({ rowIndex }) {
      const event = this.events[rowIndex];
      if (event && event.entityId) {
        this.$router.push(`/tickets/${event.entityId}`);
      }
    },
    getTicketIcon() {
      return TicketIcon;
    },
    getPatientInitials(event) {
      const first = event.patientFirstName?.charAt(0) || '';
      const last = event.patientLastName?.charAt(0) || '';
      return (first + last).toUpperCase();
    },
    getPatientName(event) {
      const first = event.patientFirstName || '';
      const last = event.patientLastName || '';
      return `${first} ${last}`.trim() || 'Unknown Patient';
    },
    eventLabel(eventType) {
      return EVENT_LABELS[eventType] || eventType;
    },
    changeList(event) {
      return [
        ...(event.changes?.fields || []).map(f => ({
          label: f.label,
          from: this.displayValue(f.field, f.from),
          to: this.displayValue(f.field, f.to)
        })),
        ...(event.changes?.items || []).flatMap(item =>
          item.changes.map(c => ({
            label: `${item.productName} — ${c.label}`,
            from: this.displayValue(c.field, c.from),
            to: this.displayValue(c.field, c.to)
          }))
        )
      ];
    },
    eventSummary(event) {
      const snapshot = event.changes?.snapshot;

      if (event.eventType === 'ticket_cancelled') {
        return `${formatUtils.formatCurrency(snapshot?.amountCentsCancelled || 0)} written off`;
      }

      if (event.eventType === 'ticket_deleted') {
        return `${formatUtils.formatCurrency(snapshot?.amountCentsTotal || 0)} deleted`;
      }

      if (event.eventType === 'ticket_updated') {
        const fieldCount = (event.changes?.fields || []).length;
        const itemCount = (event.changes?.items || []).length;
        if (fieldCount === 0 && itemCount === 0) return '';
        if (itemCount > 0 && fieldCount === 0) {
          return `${itemCount} item${itemCount === 1 ? '' : 's'} updated`;
        }
        if (fieldCount > 0 && itemCount === 0) {
          return fieldCount === 1
            ? `${event.changes.fields[0].label} updated`
            : `${fieldCount} fields updated`;
        }
        return `${fieldCount + itemCount} changes`;
      }

      return '';
    },
    snapshotLine(snapshot) {
      return [
        `Total ${formatUtils.formatCurrency(snapshot.amountCentsTotal || 0)}`,
        `Collected ${formatUtils.formatCurrency(snapshot.amountCentsPaid || 0)}`,
        `${snapshot.itemCount} item${snapshot.itemCount === 1 ? '' : 's'}`
      ].join(' · ');
    },
    eventTooltip(event) {
      const snapshot = event.changes?.snapshot;
      const lines = [];

      if (event.eventType === 'ticket_cancelled') {
        lines.push(event.reason ? `"${event.reason}"` : 'No reason given');
        if (snapshot) lines.push(this.snapshotLine(snapshot));
        return lines.join('\n');
      }

      if (event.eventType === 'ticket_deleted') {
        if (snapshot) {
          lines.push(`Was ${snapshot.wasCancelled ? 'cancelled' : 'active'} when deleted`);
          lines.push(this.snapshotLine(snapshot));
        }
        return lines.join('\n');
      }

      if (event.eventType === 'ticket_updated') {
        return this.changeList(event)
          .map(c => `${c.label}: ${c.from} → ${c.to}`)
          .join('\n');
      }

      return '';
    },
    displayValue(field, value) {
      if (value === null || value === undefined || value === '') return 'empty';
      if (field === 'amountCents') return formatUtils.formatCurrency(value);
      if (field === 'dateTicketStart') return formatUtils.formatDate(value);
      return value;
    }
  }
};
</script>

<style scoped>
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-left> :first-child {
  max-width: 300px;
  flex: 1;
  min-width: 200px;
}

.ticket-cell,
.patient-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.name-text {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-value {
  color: #333;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.details-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  min-width: 0;
}

.details-info-icon {
  color: #9ca3af;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.details-info-icon:hover {
  color: #6b7280;
}
</style>
